/** @format */

import {
  UBER_AUTH_REDIRECT_PATH,
  TAXI_QUOTE_BRANDREF,
} from "../../../config/config";
import usePanelStore from "../store/PanelStore";
import useQuoteStore from "../store/QuoteStore";
import useAppStateStore from "../../../shared/store/AppStateStore";
import NewQuoteMapper from "../mappers/NewQuoteMapper";
import * as Sentry from "@sentry/react";

export const fetchNewQuote = async (code, navigate) => {
  const brandRef = TAXI_QUOTE_BRANDREF;

  try {
    const validCode = code === null ? "NoLiveUberAccount" : code;

    const requestBody = {
      brandRef: brandRef,
      uberAuthorizationCode: validCode,
      RedirectUrl: window.location.origin + UBER_AUTH_REDIRECT_PATH,
    };

    const response = await fetch(`/api/newQuote`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const responseData = await response.json(); // May throw
      const errorObj = responseData.errors;

      let message = "fetch new quote error";
      if (errorObj && typeof errorObj === "object") {
        const errorMessages = Object.entries(errorObj)
          .filter(([key]) => key !== "$")
          .flatMap(([, value]) => (Array.isArray(value) ? value : []));
        if (errorMessages.length > 0) {
          message = errorMessages.join(", ");
        }
      }

      throw new Error(message);
    }

    const data = await response.json();
    NewQuoteMapper.set(data);
  } catch (error) {
    Sentry.captureException("Fetch new quote error:", error);
    navigate(
      `/error?message=${encodeURIComponent(
        error?.message || "Unknown error occurred"
      )}`
    );
  }
};

export const saveQuote = async (quoteData, navigate) => {
  const quote = useQuoteStore.getState();
  if (
    quoteData.risk.drivers[0].datePassedTest == null ||
    quoteData.risk.drivers[0].datePassedTest == undefined ||
    quoteData.risk.drivers[0].datePassedTest == ""
  ) {
    quoteData.risk.drivers[0].datePassedTest = "2008-01-01T00:00:00";
  }
  const url = "/api/quote/save";
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(quoteData),
    });

    const data = await response.json();
    const mainDriverIndex = data.risk.drivers.findIndex(
      (driver) => driver.mainUser
    );
    if (mainDriverIndex > 0) {
      const mainDriver = data.risk.drivers.splice(mainDriverIndex, 1)[0];
      data.risk.drivers.unshift(mainDriver);
    }
    quote.setQuote(data);
    if (response.ok) {
      return data;
    } else if (response?.status == 500) {
      navigate(`/error?message=Internal Server Error`);
    } else {
      navigate(`/error?url=${encodeURIComponent(url)}`);
    }
  } catch (error) {
    navigate(`/error?url=${encodeURIComponent(url)}`);
    Sentry.captureException("save quote error: ", error);
    console.error("Error saving quote:", error);
  }
};

export const fetchPanelData = async (
  PreSelectedQuoteAddons,
  RiskObjectId,
  startDate = null
) => {
  const url = "/api/panel";
  const panel = usePanelStore.getState();
  const brandRef = TAXI_QUOTE_BRANDREF;

  try {
    const requestBody = {
      brandRef: brandRef,
      PreSelectedQuoteAddons,
      RiskObjectId,
      RiskObjectHistoryId: 1,
    };

    // Only add startDate if it exists
    if (startDate) {
      requestBody.CoverStartDate = startDate;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    panel.set("panelData", data);

    return data;
  } catch (error) {
    console.error("Error fetching panel data:", error);
    throw error;
  }
};

export const fetchRiskData = async (riskObjectId, currentStep) => {
  const url = "/api/quote/risk";
  const brandRef = TAXI_QUOTE_BRANDREF;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        riskObjectId,
        RiskObjectHistoryId: 1,
        brandRef: brandRef,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      const quote = useQuoteStore.getState();
      quote.setQuote(data);
      // Set currentStep using AppStateStore setter
      useAppStateStore.getState().setCurrentStep(currentStep);

      const drivers = data?.risk?.drivers;
      const claims = drivers[0].claims;

      // Set additionalDriver and claims using AppStateStore setters
      const STATE = useAppStateStore.getState();
      STATE.setAdditionalDriver(drivers.length > 1 ? "Yes" : "No");
      STATE.setClaims(claims !== null && claims.length > 0 ? "Yes" : "No");
    } else {
      console.error("API call failed with status:", response.status);
    }
  } catch (error) {
    console.error("Error fetching risk data:", error);
    throw error;
  }
};

export const cloneQuote = async (cloneRiskObjectId, currentStep) => {
  const url = "/api/quote/clone";
  const brandRef = TAXI_QUOTE_BRANDREF;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        cloneRiskObjectId,
        cloneRiskObjectHistoryId: 1,
        brandRef: brandRef,
      }),
    });

    if (response.ok) {
      const data = await response.json();

      const quote = useQuoteStore.getState();
      quote.setQuote(data);

      const { updateField } = useQuoteStore.getState();

      updateField("risk.startDate", null);

      // Address fields
      updateField("risk.drivers.0.address", data.risk.drivers[0].address);
      updateField(
        "risk.drivers.0.address.postcode",
        data.risk.drivers[0].address.postcode
      );
      updateField(
        "risk.drivers.0.address.houseNo",
        data.risk.drivers[0].address.houseNo
      );

      // Set currentStep using AppStateStore setter
      useAppStateStore.getState().setCurrentStep(currentStep);

      const drivers = data?.risk?.drivers;
      const claims = drivers[0].claims;

      // Set additionalDriver and claims using AppStateStore setters
      const STATE = useAppStateStore.getState();
      STATE.setAdditionalDriver(drivers.length > 1 ? "Yes" : "No");
      STATE.setClaims(claims !== null && claims.length > 0 ? "Yes" : "No");
    } else {
      console.error("API call failed with status:", response.status);
    }
  } catch (error) {
    console.error("Error fetching risk data:", error);
    throw error;
  }
};

const CACHE_KEY = "policyDetails";
const CACHE_EXPIRATION_KEY = "policyDetailsExpiration";

export async function getSchemeDescription() {
  try {
    // Try browser cache first
    const cachedData = localStorage.getItem(CACHE_KEY);
    const cachedExpiration = localStorage.getItem(CACHE_EXPIRATION_KEY);

    if (
      cachedData &&
      cachedExpiration &&
      new Date() < new Date(cachedExpiration)
    ) {
      return JSON.parse(cachedData);
    }

    // If no valid browser cache, fetch from server with cache headers
    const response = await fetch("/api/scheme-description", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "public, max-age=86400", // 24 hours
        "If-None-Match": localStorage.getItem("schemeEtag"), // Send ETag if we have it
      },
    });

    // If server returns 304 Not Modified, use browser cache
    if (response.status === 304) {
      return JSON.parse(cachedData);
    }

    // Store new ETag from server
    const etag = response.headers.get("ETag");
    if (etag) {
      localStorage.setItem("schemeEtag", etag);
    }

    const data = await response.json();

    // Update browser cache
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(
      CACHE_EXPIRATION_KEY,
      new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    );

    return data;
  } catch (error) {
    console.error("Error fetching policy details:", error);
    throw error;
  }
}

const ADDONS_CACHE_KEY = "addonsCache";
const ADDONS_ETAG_KEY = "addonsEtag";
const ADDONS_EXPIRATION_KEY = "addonsCacheExpiration";

export async function getAddonsDescriptions() {
  try {
    // Try browser cache first
    const cachedData = localStorage.getItem(ADDONS_CACHE_KEY);
    const cachedExpiration = localStorage.getItem(ADDONS_EXPIRATION_KEY);

    if (
      cachedData &&
      cachedExpiration &&
      new Date() < new Date(cachedExpiration)
    ) {
      return JSON.parse(cachedData);
    }

    // Fetch from server with cache headers
    const response = await fetch("/api/allAddons", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Cache-Control": "public, max-age=86400", // 24 hours
        "If-None-Match": localStorage.getItem(ADDONS_ETAG_KEY),
      },
    });

    // If server returns 304 Not Modified, use browser cache
    if (response.status === 304 && cachedData) {
      return JSON.parse(cachedData);
    }

    // Store new ETag from server
    const etag = response.headers.get("ETag");
    if (etag) {
      localStorage.setItem(ADDONS_ETAG_KEY, etag);
    }

    const data = await response.json();

    // Update browser cache with expiration time
    localStorage.setItem(ADDONS_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(
      ADDONS_EXPIRATION_KEY,
      new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    );

    return data;
  } catch (error) {
    console.error("Error fetching addons descriptions:", error);
    throw error;
  }
}
export const fetchHpp = async () => {
  try {
    const response = await fetch("/api/getHpp", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Cache-Control": "public, max-age=86400", // 24 hours
        "If-None-Match": localStorage.getItem(ADDONS_ETAG_KEY),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch addons");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch HPP error:", error.message);
  }
};

export const fetchPolicyDetails = async () => {
  try {
    // Get scheme description with caching
    const data = await getSchemeDescription();

    if (!data) {
      throw new Error("No policy details received");
    }

    // Store in localStorage for components to access
    localStorage.setItem("policyDetails", JSON.stringify(data));

    return data;
  } catch (error) {
    console.error("Error fetching policy details:", error);
    throw error;
  }
};

export const generateDocuments = async (requestBody, navigate) => {
  try {
    const response = await fetch("/api/generateDocuments", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      await handleErrorResponse(response, navigate);
    }

    const data = await response.json();

    return data; // Return the fetched data
  } catch (error) {
    console.error("Error fetching addons descriptions:", error);
    throw error; // Rethrow the error to handle it in the component
  }
};
